import { useEffect, useState, type PropsWithChildren } from "react";

import { ActionButton, Button, cn, Dialog, Loader, OTPField } from "@eisox/design-system";
import { PhoneIcon } from "@eisox/icons";
import { useCountDown } from "@eisox/tools";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useMutation } from "@tanstack/react-query";

import { accountApi } from "~/api";
import { m } from "~/paraglide/messages";

const TIME_TO_RESEND = 1000 * 60;
const LENGHT_CODE = 6;

interface PhoneNumberValidationDialogProps extends PropsWithChildren, Dialog.RootProps {
  onSuccess: () => void;
}

const PhoneNumberValidationDialog: React.FC<PhoneNumberValidationDialogProps> = ({
  open: openProp,
  // oxlint-disable-next-line typescript/unbound-method
  onOpenChange,
  defaultOpen = false,
  onSuccess,
  children,
  ...props
}) => {
  const { count, restart } = useCountDown({ initialValue: TIME_TO_RESEND, limit: 0 });

  const [open, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange,
  });

  const [code, setCode] = useState("");

  const { mutate: sendCode, isPending: isSendingCode } = useMutation({
    meta: { toast: { success: m.code_send(), error: m.send_code_error() } },
    mutationFn: () => accountApi.requestPhoneValidation(),
    onMutate: () => restart(),
    onError: () => setOpen(false),
  });

  const { mutate: checkCode, isPending: isCheckingCode } = useMutation({
    meta: { toast: { success: m.phone_verified(), error: m.invalid_code() } },
    mutationFn: (code: string) => accountApi.verifyPhoneNumber(code),
    onSuccess: () => {
      setCode("");
      setOpen(false);
      onSuccess();
    },
  });

  const handleResendCode = () => {
    if (count > 0) return;
    sendCode();
    setCode("");
  };

  useEffect(() => {
    if (open) restart(); //* Start the countdown when the dialog is opened
    // oxlint-disable-next-line react/exhaustive-deps
  }, [open]);

  const isPending = isSendingCode || isCheckingCode;

  return (
    <Dialog.Root {...props} open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Dialog.Content title={m.phone_validation()} icon={<PhoneIcon />}>
        <div className="gap-2.5 flex flex-col items-center">
          {isPending ? (
            <Loader />
          ) : (
            <>
              <OTPField.Root
                maxLength={LENGHT_CODE}
                value={code}
                onChange={code => setCode(code)}
                onComplete={checkCode}
              >
                {({ slots }) => (
                  <>
                    {slots.map((slot, index) => (
                      <OTPField.Slot {...slot} key={index} />
                    ))}
                  </>
                )}
              </OTPField.Root>

              <Button
                disabled={count > 0}
                onClick={handleResendCode}
                className={cn(
                  "disabled:font-light mr-auto disabled:text-base disabled:text-primary-dark-blue disabled:italic",
                )}
              >
                {count > 0 ? m.time_resend_code({ s: count / 1000, count }) : m.resend_code()}
              </Button>
            </>
          )}
        </div>
        <Dialog.Footer>
          <Dialog.Close asChild>
            <ActionButton variant="cancel">{m.cancel()}</ActionButton>
          </Dialog.Close>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

PhoneNumberValidationDialog.displayName = "PhoneNumberValidationDialog";

export { PhoneNumberValidationDialog };
